import { useCallback, useEffect, useRef, useState } from 'react'
import { resolveAsync } from '../lib/api'
import type { AsyncState, AsyncStatus } from '../types'

/* ============================================================================
 * ХУКИ ЗАГРУЗКИ ДАННЫХ
 * Любой источник опрашивается с таймаутом; при недоступности остаются
 * последние хорошие данные и статус «деградация» — пустого экрана не будет.
 * ========================================================================= */

/**
 * Периодическая загрузка ресурса с сохранением последних успешных данных.
 *
 * @param loader функция загрузки, принимающая AbortSignal
 * @param intervalMs период опроса, мс (0 — однократная загрузка)
 * @param enabled включён ли опрос
 */
export function useAsyncResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
  enabled = true,
): AsyncState<T> & { refresh: () => void } {
  const [state, setState] = useState<AsyncState<T>>({
    status: 'idle',
    data: null,
    error: null,
    updatedAt: null,
    degradedReason: null,
  })

  // Храним загрузчик и последнее состояние в refs, чтобы эффект не перезапускался
  const loaderRef = useRef(loader)
  loaderRef.current = loader
  const stateRef = useRef(state)
  stateRef.current = state
  const aliveRef = useRef(true)
  const inFlightRef = useRef(false)

  const refresh = useCallback(() => {
    // Не запускаем параллельные запросы, если предыдущий ещё не завершён
    if (inFlightRef.current) return
    inFlightRef.current = true
    setState((prev) => ({ ...prev, status: prev.status === 'idle' ? 'loading' : prev.status }))

    resolveAsync(loaderRef.current, stateRef.current)
      .then((next) => {
        if (!aliveRef.current) return
        stateRef.current = next
        setState(next)
      })
      .finally(() => {
        inFlightRef.current = false
      })
  }, [])

  useEffect(() => {
    aliveRef.current = true
    if (enabled) refresh()

    let timer: ReturnType<typeof setInterval> | null = null
    if (enabled && intervalMs > 0) {
      timer = setInterval(refresh, intervalMs)
    }
    return () => {
      aliveRef.current = false
      if (timer) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, refresh])

  return { ...state, refresh }
}

/** Значение с задержкой — для плавной индикации телеметрии */
export function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

/** Монотонный счётчик — для ключей рендера и «тик» индикаторов */
export function useTick(intervalMs = 1000): number {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return tick
}

/** Накопление истории значений для мини-графика */
export function useHistory<T>(value: T, max = 60): T[] {
  const [items, setItems] = useState<T[]>([])
  useEffect(() => {
    setItems((prev) => [...prev, value].slice(-max))
  }, [value, max])
  return items
}

/** Символ состояния → CSS-класс состояния */
export function statusToState(status: AsyncStatus): 'active' | 'pending' | 'critical-error' | 'warning' | 'approved' {
  switch (status) {
    case 'ok':
      return 'approved'
    case 'loading':
      return 'pending'
    case 'degraded':
      return 'warning'
    case 'error':
      return 'critical-error'
    default:
      return 'active'
  }
}
